import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Spinner from 'sushiweb/Spinner';
import { red } from 'sushiweb/tokens/color';
import { getGiftCardDetails } from '../../api/giftCard';
import { decryptData } from '../../helpers';
import { GcStatusEnum } from '../common/components/constants';
import {
  Container,
  Header,
  HeaderGradient,
  HeaderLogo,
  HeaderTitle,
  LoadingContent,
  SpinnerContainer,
  LoadingTitle,
  LoadingSubtitle,
} from './styles';
import { E_LOGO_WHITE } from '../../constants/images';

const hashKey = process.env.REACT_APP_GIFT_CARD_HASH_KEY || '11111111111111111';

const GiftCardGenerating = ({ referenceId, onStatusChange }) => {
  const pollingIntervalRef = useRef(null);
  const pollingStartTimeRef = useRef(null);

  const fetchGiftCardData = async () => {
    try {
      const res = await getGiftCardDetails(referenceId);
      const { statusResponse } = res || {};

      if (!statusResponse?.status) {
        console.error('Failed to fetch gift card details during polling');
        return;
      }

      const {
        gift_card_status: giftCardStatus,
        encrypted_payload: encryptedPayload,
      } = res;

      // If status changed from PROCESSING, stop polling and update parent
      if (giftCardStatus !== GcStatusEnum.PROCESSING) {
        stopPolling();

        let decryptedData = null;
        if (encryptedPayload) {
          decryptedData = await decryptData(encryptedPayload, hashKey);
        }

        onStatusChange(giftCardStatus, decryptedData);
      }
    } catch (error) {
      console.error('Gift card fetch error during polling:', error);
    }
  };

  const startPolling = () => {
    pollingStartTimeRef.current = Date.now();

    pollingIntervalRef.current = setInterval(() => {
      const elapsedTime = Date.now() - pollingStartTimeRef.current;

      if (elapsedTime >= 10000) {
        stopPolling();
        onStatusChange(GcStatusEnum.TIMEOUT);
        return;
      }

      fetchGiftCardData();
    }, 1000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      pollingStartTimeRef.current = null;
    }
  };

  useEffect(() => {
    startPolling();

    return () => {
      stopPolling();
    };
  }, []);

  return (
    <Container>
      <Header>
        <HeaderGradient>
          <HeaderLogo src={E_LOGO_WHITE} alt='Eternal Logo' />
          <HeaderTitle>Happy Diwali from all of us at Eternal</HeaderTitle>
        </HeaderGradient>
      </Header>

      <LoadingContent>
        <SpinnerContainer>
          <Spinner color={red.z_red} size='large' />
        </SpinnerContainer>
        <LoadingTitle>Generating your voucher</LoadingTitle>
        <LoadingSubtitle>
          Please wait while we prepare your gift card...
        </LoadingSubtitle>
      </LoadingContent>
    </Container>
  );
};

GiftCardGenerating.propTypes = {
  referenceId: PropTypes.string.isRequired,
  onStatusChange: PropTypes.func.isRequired,
};

export default GiftCardGenerating;
